import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { ChartSettingsContext, DEFAULT_CHART_SETTINGS } from "./chartSettingsContext";
import type { ChartSettings } from "./types";

/**
 * Chart preferences, loaded once and shared by every chart in the app.
 *
 * Only the provider lives here. The context, the hook and the defaults are in
 * chartSettingsContext.ts so this file exports a component and nothing else,
 * which is what fast refresh needs to swap it without remounting the tree.
 */
export function ChartSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<ChartSettings>(DEFAULT_CHART_SETTINGS);

  useEffect(() => {
    // A failed load leaves the defaults in place: charts drawing with the
    // standard averages beats charts not drawing.
    api
      .chartSettings()
      .then(setSettings)
      .catch(() => {});
  }, []);

  const save = useCallback(async (next: ChartSettings) => {
    setSettings(await api.saveChartSettings(next));
  }, []);

  return (
    <ChartSettingsContext.Provider value={{ settings, save }}>
      {children}
    </ChartSettingsContext.Provider>
  );
}
